import React, { useEffect, useRef } from 'react';
import { Drawer, Spin, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import WalletIcon from '@assets/icons/certificates/wallet/wallet.svg';
import { useCertificatesWallet } from '@share/components';
import { Routes } from '@share/constants';
import { CertificateStatusEnum } from '@share/common-types';
import type { FunctionalRootState } from '@share/utils';

import { WALLET_DRAWER_TITLE_ID, WALLET_SECTION_TITLE_ID } from './constants';
import type { WalletDrawerProps } from './types';
import { WalletCard } from './components';

import styles from './styles.module.scss';

const DEFAULT_STATUSES: CertificateStatusEnum[] = [
  CertificateStatusEnum.Active,
  CertificateStatusEnum.Redeemed,
  CertificateStatusEnum.PartiallyRedeemed,
  CertificateStatusEnum.Expired,
  CertificateStatusEnum.Canceled,
  CertificateStatusEnum.Pending,
];

export const WalletDrawer = ({
  isOpen,
  onClose,
  accountUsername,
}: WalletDrawerProps): React.ReactElement => {
  const intl = useIntl();
  const { user } = useSelector((state: FunctionalRootState) => state.loginStore);
  const previousBodyOverflowRef = useRef<string>('');
  const { certificates, loading, error, fetchCertificates } = useCertificatesWallet({
    statuses: DEFAULT_STATUSES,
    autoFetch: isOpen,
    email: user?.email,
    organizationId: user?.userAdditionalData?.organizationId,
  });
  const certificatesPath: string = accountUsername
    ? `/${accountUsername}${Routes.Certificates}`
    : Routes.Certificates;
  const walletIconAlt = intl.formatMessage({ id: 'certificates.wallet.iconAlt' });
  const closeWalletAriaLabel = intl.formatMessage({ id: 'certificates.wallet.closeAriaLabel' });

  useEffect((): (() => void) | void => {
    if (!isOpen) {
      return;
    }
    // for prevent scroll body when open drawer
    previousBodyOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return (): void => {
      document.body.style.overflow = previousBodyOverflowRef.current;
    };
  }, [isOpen]);

  return (
    <Drawer
      className={styles.walletDrawer}
      placement="right"
      width={560}
      zIndex={4100}
      onClose={onClose}
      open={isOpen}
      destroyOnClose={false}
    >
      <header className={styles.header}>
        <div className={styles.titleWrap}>
          <img src={WalletIcon} alt={walletIconAlt} aria-hidden />
          <h2>
            <FormattedMessage id={WALLET_DRAWER_TITLE_ID} />
          </h2>
        </div>
        <button
          className={styles.closeButton}
          type="button"
          onClick={onClose}
          aria-label={closeWalletAriaLabel}
        >
          <CloseOutlined />
        </button>
      </header>

      <section className={styles.content}>
        <h3>
          <FormattedMessage id={WALLET_SECTION_TITLE_ID} />
        </h3>

        {loading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : error ? (
          <div className={`${styles.stateWrap} ${styles.error}`}>
            <p className={styles.stateText}>
              <FormattedMessage id="certificates.wallet.errorState" />
            </p>
            <Button className={styles.stateButton} type="primary" onClick={fetchCertificates}>
              <FormattedMessage id="certificates.wallet.refetch" />
            </Button>
          </div>
        ) : certificates.length === 0 ? (
          <div className={`${styles.stateWrap} ${styles.empty}`}>
            <p className={styles.stateText}>
              <FormattedMessage id="certificates.wallet.emptyState" />
            </p>
            <Link to={certificatesPath}>
              <Button className={styles.stateButton} type="primary">
                <FormattedMessage id="certificates.wallet.getCertificate" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className={styles.list}>
            {certificates.map((certificate) => (
              <WalletCard
                key={certificate.id}
                certificate={certificate}
                accountUsername={accountUsername ?? ''}
                fetchCertificates={fetchCertificates}
              />
            ))}
          </div>
        )}
      </section>
    </Drawer>
  );
};
